<script setup lang="ts">
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import type { ButtonV2Props } from './button-v2';
import TTooltip from '../TTooltip.vue';

const props = withDefaults(defineProps<ButtonV2Props>(), {
  variant: 'primary',
});

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const handleClick = (event: MouseEvent) => {
  emits('click', event);
};
</script>

<template>
  <TTooltip :content="props.tooltip" placement="top">
    <button
      :type="props.type || 'button'"
      :disabled="props.disabled || props.loading"
      class="touch-manipulation text-nowrap disabled:cursor-not-allowed disabled:opacity-50"
      :class="[
        'inline-flex items-center gap-1 rounded-lg font-semibold transition-all duration-300',
        props.size === 'md'
          ? 'text-md h-10 px-4 py-2'
          : 'h-8 px-3 py-1.5 text-sm',
        props.variant === 'primary' &&
          'bg-gradient-to-tr from-violet-600 to-indigo-600 text-white shadow-[0_4px_15px_rgba(147,51,234,0.4)] ease-[cubic-bezier(0.4,0,0.2,1)] hover:translate-y-[-1px] hover:shadow-[0_8px_25px_rgba(147,51,234,0.5)] hover:brightness-110 active:translate-y-[1px] active:shadow-[0_2px_10px_rgba(147,51,234,0.3)]',
        props.variant === 'danger' &&
          'bg-danger-500 text-white hover:brightness-110 active:translate-y-[1px]',
        props.variant === 'neutral' &&
          'border border-slate-200 bg-slate-50 text-slate-800',
        props.variant === 'ghost-primary' &&
          'text-primary-500 hover:bg-primary-50',
        props.variant === 'ghost-neutral' &&
          'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800',
      ]"
      @click="handleClick"
    >
      <HugeIcon
        v-if="props.icon && props.iconPosition !== 'right'"
        :spin="props.loading"
        :icon="props.loading ? 'loading' : props.icon"
        :size="props.size === 'sm' ? 20 : 24"
      />
      <!-- Wrap label in a span that can shrink (min-w-0) and apply
      truncate so text-overflow: ellipsis works inside flex containers -->
      <span
        class="max-w-full min-w-0 truncate"
        :class="hideLabelOnSmallScreens ? 'hidden sm:block' : 'block'"
        >{{ props.label }}</span
      >
      <HugeIcon
        v-if="props.icon && props.iconPosition === 'right'"
        :spin="props.loading"
        :icon="props.loading ? 'loading' : props.icon"
        :size="props.size === 'sm' ? 20 : 24"
      />
    </button>
  </TTooltip>
</template>
